import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  applyDepotWarehouseToDoPreparations,
  applyDoPreparationWarehouse,
  createDoBatchPlan,
  createDoFromSource,
  getDoBatchExecution,
  getDoPreparationDetail,
  getDoPreparationFleetOrders,
  getDoBatchPlan,
  getDoWriteContract,
  getDoWriteProfileStatus,
  getDoSourceWriteHistory,
  pauseDoBatchExecution,
  recalculateDoStock,
  reconcileDoSourceWrite,
  refreshDoPreparationLiveStock,
  resumeDoBatchExecution,
  startDoBatchExecution,
  startDoReconciliation,
  startDoReconciliationBatch,
  syncDoPreparationItems,
  terminateDoBatchExecution,
  triggerDoStockSync,
  updateDoBatchPlanPriority,
  updateDoDepotWarehouseMapping,
  updateDoPreparationDefaultWarehouse,
  updateDoPreparationItemWarehouse,
} from "../api/client.js";
import { Icon } from "../components/Icon.jsx";
import {
  DoActiveFilterSummary,
  DoKpiCard,
  DoPreparationsCommandHeader,
  DoPreparationsFilterWorkbench,
  DoPreparationsKpiStrip,
  DoPreparationsListHeader,
  DoPreparationsWorkspaceTabs,
} from "../components/do-preparations/DoPreparationsWorkspace.jsx";
import {
  DoDetailCommandCard,
  DoDetailToolbar,
  DoOperationalCheckpoints,
  DoOperationalException,
} from "../components/do-preparations/DoPreparationDetailShell.jsx";
import {
  DoBatchPlanDialog,
  DoBatchPlanPanel,
} from "../components/do-preparations/DoBatchPlanPanel.jsx";
import { DoPreparationMasterDetail } from "../components/do-preparations/DoUnifiedReadinessWorkspace.jsx";
import {
  DoWriteContractPreview,
  DoWriteSourcePreviewDialog,
} from "../components/do-preparations/DoWriteContractPreview.jsx";
import { RouteBadge } from "../components/RouteBadge.jsx";
import { ResponsiveFilterPanel } from "../components/ResponsiveFilterPanel.jsx";
import { SortableHeader } from "../components/SortableHeader.jsx";
import { useManualSync } from "../hooks/useManualSync.js";
import { useSortableData } from "../hooks/useSortableData.js";
import { formatVolume, formatWeight } from "../utils/number-format.js";

const DO_STATUS_LABELS = {
  BELUM_DIBUAT: "Belum dibuat",
  SEBAGIAN_DIBUAT: "Sebagian dibuat",
  SELESAI: "Selesai",
  PERLU_CEK: "Perlu cek",
  STOK_KURANG: "Stok kurang",
  GAGAL_SEBAGIAN: "Gagal sebagian",
  NOT_PREPARED: "Belum disiapkan",
  READY: "Siap direview",
  BLOCKED_STOCK: "Perlu review Qty",
  DO_CREATED: "DO dibuat",
  PARTIAL: "Sebagian dibuat",
  ERROR: "Gagal",
  NO_SALES_ORDERS: "Tidak dapat diproses",
};

const STOCK_STATUS_LABELS = {
  BELUM_DICEK: "Belum dicek",
  BELUM_DIHITUNG: "Belum dihitung",
  AMAN: "Aman",
  KURANG: "Kurang",
  KOSONG: "Kosong",
  NOT_APPLICABLE: "—",
  PERLU_MAPPING_UOM: "Perlu mapping UOM",
  PERLU_DATA_SKU: "Perlu data SKU",
  PERLU_CEK: "Perlu cek",
  TIDAK_ADA_STOK: "Tidak ada stok",
  UOM_BELUM_DIPETAKAN: "UOM belum dipetakan",
  SKU_LOGISTICS_BELUM_ADA: "Data SKU belum ada",
  NO_SALES_ORDERS: "Tidak ada SO",
};

function normalizeStatusClass(value, fallback = "unknown") {
  return String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-");
}

function formatDate(value) {
  if (!value) return "—";

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "—";

  return new Intl.DateTimeFormat("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function formatTimestamp(value) {
  if (!value) return "Belum pernah";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Belum pernah";

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

function isLegacyLog(value) {
  return String(value || "").trim().toUpperCase() === "LOG";
}

function displayDriverName(value) {
  const normalized = String(value || "").trim();
  return normalized && !isLegacyLog(normalized)
    ? normalized
    : "Driver belum ditentukan";
}

function displayVehicle(value, vehicleType) {
  const plate = String(value || "").trim();
  const type = String(vehicleType || "").trim();

  if (!plate || isLegacyLog(plate)) return "Kendaraan belum ditentukan";
  return type ? `${plate} · ${type}` : plate;
}

function operationalDoError(error, fallback = "Proses Pembuatan DO belum dapat diselesaikan.") {
  const message = String(error?.message || error || "").trim();
  const normalized = message.toLowerCase();

  if (
    normalized.includes("too many subrequests") ||
    normalized.includes("workers/wrangler/configuration/#limits") ||
    normalized.includes("error 1102")
  ) {
    return "Sinkronisasi stok belum selesai. Tidak ada Delivery Order yang dibuat. Coba kembali setelah pembaruan stok selesai.";
  }

  return message || fallback;
}

function DoStatusBadge({ status }) {
  const normalizedStatus = String(status || "PERLU_CEK").toUpperCase();

  return (
    <span
      className={`do-status do-status--${normalizeStatusClass(
        normalizedStatus,
        "perlu_cek",
      )}`}
    >
      {DO_STATUS_LABELS[normalizedStatus] || status || "Perlu cek"}
    </span>
  );
}

function StockStatusBadge({ status }) {
  const normalizedStatus = String(status || "BELUM_DICEK").toUpperCase();
  const visualStatus = normalizedStatus === "KOSONG" ? "TIDAK_ADA_STOK" : normalizedStatus;

  return (
    <span
      className={`stock-readiness stock-readiness--${normalizeStatusClass(
        visualStatus,
        "belum_dicek",
      )}`}
    >
      {STOCK_STATUS_LABELS[normalizedStatus] || status || "Belum dicek"}
    </span>
  );
}

function isInteractiveTarget(target) {
  return Boolean(target.closest("button, a, input, select, textarea, label"));
}

const PROCESS_STATUS_LABELS = {
  BELUM_DIBUAT: "Belum dibuat",
  SEBAGIAN_DIBUAT: "Sebagian dibuat",
  PERLU_TINDAK_LANJUT: "Perlu tindak lanjut",
  LENGKAP: "Lengkap",
};

const FLEET_STATUS_META = {
  DRAFT: { label: "Belum siap", description: "Fleet Order masih dalam tahap persiapan", tone: "neutral" },
  SCHEDULED: { label: "Belum berangkat", description: "Perjalanan belum dimulai", tone: "neutral" },
  READY: { label: "Siap berangkat", description: "Fleet Order siap memulai perjalanan", tone: "ready" },
  READY_TO_DEPART: { label: "Siap berangkat", description: "Fleet Order siap memulai perjalanan", tone: "ready" },
  IN_PROGRESS: { label: "Sedang dikirim", description: "Pengiriman sedang berjalan", tone: "active" },
  RETURNING: { label: "Kembali ke depot", description: "Perjalanan pengiriman sudah selesai", tone: "active" },
  POST_TRIP_CHECK: { label: "Pemeriksaan akhir", description: "Kendaraan sedang menjalani pemeriksaan akhir", tone: "active" },
  COMPLETED: { label: "Pengiriman selesai", description: "Perjalanan Fleet Order sudah selesai", tone: "complete" },
  CANCELLED: { label: "Dibatalkan", description: "Fleet Order tidak dilanjutkan", tone: "cancelled" },
};

function getDoWorkspaceStatus(row) {
  if (String(row.fleetStatus || "").toUpperCase() === "CANCELLED") {
    return "CANCELLED";
  }

  if (String(row.doStatus || "").toUpperCase() === "SELESAI") {
    return "COMPLETED";
  }

  return "PENDING";
}

function getDoProcessStatus(row) {
  const workspaceStatus = row.workspaceStatus || getDoWorkspaceStatus(row);

  if (workspaceStatus === "COMPLETED") return "LENGKAP";

  if (workspaceStatus === "CANCELLED") {
    if (Number(row.totalSo) > 0 && Number(row.doCreated) >= Number(row.totalSo)) {
      return "LENGKAP";
    }
    if (Number(row.doCreated) > 0) return "SEBAGIAN_DIBUAT";
    return "BELUM_DIBUAT";
  }

  const doStatus = String(row.doStatus || "").toUpperCase();
  const stockStatus = String(row.stockStatus || "").toUpperCase();
  const reconciliationStatus = String(row.reconciliation?.status || "").toUpperCase();

  const needsAttention =
    ["GAGAL_SEBAGIAN", "NO_SALES_ORDERS", "PERLU_CEK", "STOK_KURANG"].includes(
      doStatus,
    ) ||
    [
      "KURANG",
      "KOSONG",
      "PERLU_CEK",
      "PERLU_MAPPING_UOM",
      "PERLU_DATA_SKU",
      "NO_SALES_ORDERS",
    ].includes(stockStatus) ||
    ["FAILED", "PARTIAL_ERROR"].includes(reconciliationStatus);

  if (needsAttention) return "PERLU_TINDAK_LANJUT";
  if (Number(row.doCreated) > 0) return "SEBAGIAN_DIBUAT";
  return "BELUM_DIBUAT";
}

function ProcessStatusBadge({ status }) {
  const normalized = String(status || "BELUM_DIBUAT").toUpperCase();
  const visualStatus =
    normalized === "PERLU_TINDAK_LANJUT"
      ? "PERLU_CEK"
      : normalized === "LENGKAP"
        ? "SELESAI"
        : normalized;

  return (
    <span className={`do-status do-status--${normalizeStatusClass(visualStatus)}`}>
      {PROCESS_STATUS_LABELS[normalized] || status || "Belum dibuat"}
    </span>
  );
}

function getFleetStatusMeta(status) {
  const normalized = String(status || "").trim().toUpperCase();
  return (
    FLEET_STATUS_META[normalized] || {
      label: "Status belum tersedia",
      description: "Periksa detail Fleet Order untuk status terbaru",
      tone: "neutral",
    }
  );
}

function FleetStatusBadge({ status }) {
  const meta = getFleetStatusMeta(status);

  return (
    <span className={`do-fleet-status do-fleet-status--${meta.tone}`}>
      {meta.label}
    </span>
  );
}

const DO_WORKSPACE_LABELS = {
  PENDING: "Perlu Diproses",
  COMPLETED: "DO Lengkap",
  CANCELLED: "Dibatalkan",
};

const DO_WORKSPACE_TITLES = {
  PENDING: "Fleet Order yang Perlu Diproses",
  COMPLETED: "Fleet Order dengan DO Lengkap",
  CANCELLED: "Fleet Order Dibatalkan",
};

const DO_WORKSPACE_DESCRIPTIONS = {
  PENDING:
    "Seluruh Fleet Order aktif yang belum selesai dari sisi DO, termasuk yang belum dibuat, sebagian dibuat, atau perlu tindak lanjut.",
  COMPLETED: "Tinjau Fleet Order yang seluruh Sales Order-nya sudah memiliki Delivery Order.",
  CANCELLED: "Tinjau Fleet Order yang dibatalkan tanpa mencampurnya dengan pekerjaan aktif.",
};

const RECONCILIATION_STATUS_LABELS = {
  QUEUED: "Menunggu pemeriksaan",
  RUNNING: "Sedang memeriksa",
  COMPLETED: "Pemeriksaan selesai",
  PARTIAL_ERROR: "Selesai dengan kendala",
  FAILED: "Pemeriksaan gagal",
};

function getReconciliationDescription(row) {
  const reconciliation = row?.reconciliation || {};

  if (reconciliation.active) {
    const pageText = reconciliation.pageCount
      ? ` · halaman ${Math.min(reconciliation.scannedPages + 1, reconciliation.pageCount)}/${reconciliation.pageCount}`
      : "";
    return `Memeriksa ${reconciliation.resolvedCount}/${reconciliation.targetCount} SO${pageText}`;
  }

  if (reconciliation.freshnessStatus === "NEVER") return "Belum pernah diperiksa lengkap";
  if (reconciliation.freshnessStatus === "STALE") return "Data DO mungkin belum terbaru";
  if (reconciliation.status === "PARTIAL_ERROR") return "Pemeriksaan terakhir memiliki kendala";
  return formatTimestamp(reconciliation.completedAt || row?.lastCheckedAt);
}

const PROCESS_STATUS_OPTIONS = [
  "BELUM_DIBUAT",
  "SEBAGIAN_DIBUAT",
  "PERLU_TINDAK_LANJUT",
];

function normalizeWorkspace(value) {
  const normalized = String(value || "").toUpperCase();
  if (["COMPLETED", "CANCELLED"].includes(normalized)) return normalized;
  return "PENDING";
}

function normalizeListFilters(value = {}) {
  const legacyProcessStatus = value.processStatus || value.doStatus || "";
  return {
    search: String(value.search || ""),
    depotId: String(value.depotId || ""),
    driverKey: String(value.driverKey || ""),
    processStatus: String(legacyProcessStatus || ""),
    foRouteId: String(value.foRouteId || ""),
    departureDate: String(value.departureDate || ""),
  };
}

function getDriverFilterKey(row) {
  const driverId = Number(row?.driverId);
  if (Number.isInteger(driverId) && driverId > 0) return `id:${driverId}`;

  const displayName = displayDriverName(row?.driverName);
  return `name:${displayName.toLocaleLowerCase("id-ID")}`;
}

function matchesOperationalFilters(row, filterState, { ignore = "" } = {}) {
  if (ignore !== "depotId" && filterState.depotId) {
    if (String(row.depotId || "") !== String(filterState.depotId)) return false;
  }

  if (ignore !== "driverKey" && filterState.driverKey) {
    if (getDriverFilterKey(row) !== filterState.driverKey) return false;
  }

  if (ignore !== "processStatus" && filterState.processStatus) {
    if (row.processStatus !== filterState.processStatus) return false;
  }

  if (ignore !== "foRouteId" && filterState.foRouteId) {
    if (String(row.foRouteId || "") !== String(filterState.foRouteId)) return false;
  }

  if (ignore !== "departureDate" && filterState.departureDate) {
    if (String(row.departureDate || "") !== String(filterState.departureDate)) return false;
  }

  return true;
}

function uniqueFacetOptions(rows, valueForRow, labelForRow) {
  const values = new Map();

  for (const row of rows) {
    const value = String(valueForRow(row) || "");
    if (!value || values.has(value)) continue;
    values.set(value, String(labelForRow(row) || value));
  }

  return [...values.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((left, right) => left.label.localeCompare(right.label, "id-ID"));
}

function preserveSelectedFacetOption(options, selectedValue, rows, valueForRow, labelForRow) {
  const value = String(selectedValue || "");
  if (!value || options.some((option) => option.value === value)) return options;

  const selectedRow = rows.find((row) => String(valueForRow(row) || "") === value);
  if (!selectedRow) return options;

  return [
    ...options,
    { value, label: String(labelForRow(selectedRow) || value) },
  ].sort((left, right) => left.label.localeCompare(right.label, "id-ID"));
}

const DO_DETAIL_PATH = /^\/pembuatan-do\/([^/]+)\/?$/;
const DO_LIST_STATE_KEY = "route-planning:do-preparation-list-state";

function readDoDetailLocation() {
  const match = window.location.pathname.match(DO_DETAIL_PATH);

  if (!match) return null;

  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function buildDoDetailUrl(id) {
  return `/pembuatan-do/${encodeURIComponent(id)}`;
}

function clearDoListReturnState() {
  try {
    window.sessionStorage.removeItem(DO_LIST_STATE_KEY);
  } catch {
    // Pembuatan DO must remain usable when storage is unavailable.
  }
}

function readDoListReturnState() {
  // The stored snapshot is only a temporary bridge while the user is inside a
  // Pembuatan DO detail. A fresh list visit / refresh must start clean instead
  // of resurrecting an older filter selection.
  if (!readDoDetailLocation()) {
    clearDoListReturnState();
    return null;
  }

  try {
    const stored = window.sessionStorage.getItem(DO_LIST_STATE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveDoListReturnState(value) {
  try {
    window.sessionStorage.setItem(DO_LIST_STATE_KEY, JSON.stringify(value));
  } catch {
    // Pembuatan DO must remain usable when storage is unavailable.
  }
}

function publishCommittedLocation(url, state) {
  window.dispatchEvent(
    new CustomEvent("route-planning:location-committed", {
      detail: { state, url },
    }),
  );
}

function DoPreparationList({ initialListState = {}, onOpen }) {
  const initialFilters = normalizeListFilters(initialListState.filters || {});
  const [rows, setRows] = useState([]);
  const [workspace, setWorkspace] = useState(
    normalizeWorkspace(initialListState.workspace),
  );
  const [filters, setFilters] = useState(initialFilters);
  const [draft, setDraft] = useState(
    normalizeListFilters(initialListState.draft || initialFilters),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reconcileNotice, setReconcileNotice] = useState("");
  const [reconcileRequested, setReconcileRequested] = useState(false);

  const load = useCallback(async (next, { silent = false, autoStart = true } = {}) => {
    if (!silent) setLoading(true);
    setError("");

    try {
      const payload = await getDoPreparationFleetOrders({
        search: next.search,
        includeCancelled: true,
      });
      const nextRows = payload.fleetOrders || [];
      setRows(nextRows);

      if (!autoStart) {
        const stillActive = nextRows.some((row) => row.reconciliation?.active);
        setReconcileRequested(stillActive);
        if (!stillActive) setReconcileNotice("");
      }

      if (autoStart) {
        const staleFleetOrderIds = nextRows
          .filter(
            (row) =>
              row.fleetStatus !== "CANCELLED" &&
              row.doStatus !== "SELESAI" &&
              !row.reconciliation?.active &&
              ["NEVER", "STALE"].includes(row.reconciliation?.freshnessStatus),
          )
          .slice(0, 5)
          .map((row) => row.id);

        if (staleFleetOrderIds.length) {
          setReconcileNotice(
            `Memperbarui status DO untuk ${staleFleetOrderIds.length} Fleet Order di latar belakang.`,
          );
          setReconcileRequested(true);
          startDoReconciliationBatch(staleFleetOrderIds, {
            freshnessMinutes: 5,
          })
            .then(() => {
              window.setTimeout(() => {
                load(next, { silent: true, autoStart: false });
              }, 350);
            })
            .catch((requestError) => {
              setReconcileRequested(false);
              setError(requestError.message);
            });
        }
      }
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(filters).catch((requestError) => setError(requestError.message));
  }, [load]);

  const hasActiveReconciliation = rows.some(
    (row) => row.reconciliation?.active,
  );

  useEffect(() => {
    if (!hasActiveReconciliation && !reconcileRequested) return undefined;

    const interval = window.setInterval(() => {
      load(filters, { silent: true, autoStart: false });
    }, 2000);

    return () => window.clearInterval(interval);
  }, [filters, hasActiveReconciliation, load, reconcileRequested]);

  const classifiedRows = useMemo(
    () =>
      rows.map((row) => {
        const workspaceStatus = getDoWorkspaceStatus(row);
        const nextRow = { ...row, workspaceStatus };
        return {
          ...nextRow,
          processStatus: getDoProcessStatus(nextRow),
        };
      }),
    [rows],
  );

  const workspaceRows = useMemo(
    () => classifiedRows.filter((row) => row.workspaceStatus === workspace),
    [classifiedRows, workspace],
  );

  const workspaceCounts = useMemo(
    () =>
      classifiedRows.reduce(
        (result, row) => {
          result[row.workspaceStatus] += 1;
          return result;
        },
        { PENDING: 0, COMPLETED: 0, CANCELLED: 0 },
      ),
    [classifiedRows],
  );

  const filteredRows = useMemo(
    () =>
      workspaceRows.filter((row) =>
        matchesOperationalFilters(row, filters),
      ),
    [filters, workspaceRows],
  );

  const { sortedItems, sort, toggleSort } = useSortableData(
    filteredRows,
    initialListState.sort?.key || "departureDate",
    initialListState.sort?.direction || "desc",
  );

  function openDetail(id) {
    onOpen(id, {
      draft,
      filters,
      scrollY: window.scrollY,
      sort,
      workspace,
    });
  }

  const summary = useMemo(() => {
    const operationalRows = classifiedRows.filter(
      (row) => row.workspaceStatus !== "CANCELLED",
    );
    const pendingRows = operationalRows.filter(
      (row) => row.workspaceStatus === "PENDING",
    );

    return {
      total: operationalRows.length,
      pending: pendingRows.length,
      stockProblem: pendingRows.filter((row) =>
        ["KURANG", "KOSONG"].includes(String(row.stockStatus || "").toUpperCase()),
      ).length,
      partial: pendingRows.filter(
        (row) => row.processStatus === "SEBAGIAN_DIBUAT",
      ).length,
      actionRequired: pendingRows.filter(
        (row) => row.processStatus === "PERLU_TINDAK_LANJUT",
      ).length,
      completed: operationalRows.filter((row) => row.workspaceStatus === "COMPLETED")
        .length,
      checking: operationalRows.filter((row) => row.reconciliation?.active).length,
    };
  }, [classifiedRows]);

  const lastUpdatedAt = useMemo(() => {
    const timestamps = classifiedRows
      .map((row) => row.reconciliation?.completedAt || row.lastCheckedAt)
      .filter(Boolean)
      .map((value) => new Date(value))
      .filter((value) => !Number.isNaN(value.getTime()))
      .sort((left, right) => right.getTime() - left.getTime());

    return timestamps.length ? formatTimestamp(timestamps[0].toISOString()) : "";
  }, [classifiedRows]);

  const facetFilters = useMemo(
    () => ({
      ...draft,
      processStatus: workspace === "PENDING" ? draft.processStatus : "",
    }),
    [draft, workspace],
  );

  const depotOptions = useMemo(() => {
    const candidates = workspaceRows.filter((row) =>
      matchesOperationalFilters(row, facetFilters, { ignore: "depotId" }),
    );
    const options = uniqueFacetOptions(
      candidates,
      (row) => row.depotId,
      (row) => row.depotName || "Depot belum tersedia",
    );
    return preserveSelectedFacetOption(
      options,
      draft.depotId,
      workspaceRows,
      (row) => row.depotId,
      (row) => row.depotName || "Depot belum tersedia",
    );
  }, [draft.depotId, facetFilters, workspaceRows]);

  const driverOptions = useMemo(() => {
    const candidates = workspaceRows.filter((row) =>
      matchesOperationalFilters(row, facetFilters, { ignore: "driverKey" }),
    );
    const options = uniqueFacetOptions(
      candidates,
      getDriverFilterKey,
      (row) => displayDriverName(row.driverName),
    );
    return preserveSelectedFacetOption(
      options,
      draft.driverKey,
      workspaceRows,
      getDriverFilterKey,
      (row) => displayDriverName(row.driverName),
    );
  }, [draft.driverKey, facetFilters, workspaceRows]);

  const processStatusOptions = useMemo(() => {
    if (workspace !== "PENDING") return [];
    const candidates = workspaceRows.filter((row) =>
      matchesOperationalFilters(row, facetFilters, { ignore: "processStatus" }),
    );
    const available = new Set(candidates.map((row) => row.processStatus));
    if (draft.processStatus) available.add(draft.processStatus);
    return PROCESS_STATUS_OPTIONS.filter((status) => available.has(status));
  }, [draft.processStatus, facetFilters, workspace, workspaceRows]);

  const routeOptions = useMemo(() => {
    const candidates = workspaceRows.filter((row) =>
      matchesOperationalFilters(row, facetFilters, { ignore: "foRouteId" }),
    );
    const options = uniqueFacetOptions(
      candidates,
      (row) => row.foRouteId,
      (row) => row.foRouteName || "Rute belum tersedia",
    );
    return preserveSelectedFacetOption(
      options,
      draft.foRouteId,
      workspaceRows,
      (row) => row.foRouteId,
      (row) => row.foRouteName || "Rute belum tersedia",
    );
  }, [draft.foRouteId, facetFilters, workspaceRows]);

  const activeFilterLabels = useMemo(() => {
    const labels = [];
    const depot = classifiedRows.find(
      (row) => String(row.depotId || "") === String(filters.depotId),
    );
    const driver = classifiedRows.find(
      (row) => getDriverFilterKey(row) === filters.driverKey,
    );
    const route = classifiedRows.find(
      (row) => String(row.foRouteId || "") === String(filters.foRouteId),
    );

    if (filters.search) labels.push(`Pencarian: ${filters.search}`);
    if (depot) labels.push(`Depot: ${depot.depotName || "Depot belum tersedia"}`);
    if (driver) labels.push(`Driver: ${displayDriverName(driver.driverName)}`);
    if (workspace === "PENDING" && filters.processStatus) {
      labels.push(
        `Status: ${PROCESS_STATUS_LABELS[filters.processStatus] || filters.processStatus}`,
      );
    }
    if (route) labels.push(`Rute: ${route.foRouteName || "Rute belum tersedia"}`);
    if (filters.departureDate) {
      labels.push(`Berangkat: ${formatDate(filters.departureDate)}`);
    }

    return labels;
  }, [classifiedRows, filters, workspace]);

  function applyFilters(event) {
    event.preventDefault();

    const next = normalizeListFilters({
      ...draft,
      search: draft.search.trim(),
      processStatus: workspace === "PENDING" ? draft.processStatus : "",
    });
    const searchChanged = next.search !== filters.search;

    setDraft(next);
    setFilters(next);
    if (searchChanged) load(next);
  }

  function clearFilters() {
    const next = normalizeListFilters();
    const hadAppliedSearch = Boolean(filters.search);

    setDraft(next);
    setFilters(next);
    if (hadAppliedSearch) load(next);
  }

  function changeWorkspace(nextWorkspace) {
    const normalized = normalizeWorkspace(nextWorkspace);
    setWorkspace(normalized);

    if (normalized !== "PENDING") {
      setDraft((current) => ({ ...current, processStatus: "" }));
      setFilters((current) => ({ ...current, processStatus: "" }));
    }
  }

  const operationalColumnLabel =
    workspace === "PENDING" ? "Kesiapan Stok" : "Status Pengiriman";

  return (
    <main className="do-page do-control-page">
      <DoPreparationsCommandHeader
        contextLabel={DO_WORKSPACE_LABELS[workspace]}
        loading={loading}
        onRefresh={() => load(filters)}
        totalCount={filteredRows.length}
        updatedAt={lastUpdatedAt}
      />

      <div className="do-workspace-shell">
        {error && <div className="error-banner">{error}</div>}

        {(hasActiveReconciliation || reconcileNotice) && (
          <div className="do-reconciliation-banner" aria-live="polite">
            <Icon name="sync" size={16} />
            <div>
              <strong>Sinkronisasi status Delivery Order berjalan</strong>
              <span>
                {hasActiveReconciliation
                  ? `${summary.checking} Fleet Order sedang diperiksa. Daftar diperbarui otomatis tanpa menunggu seluruh proses selesai.`
                  : reconcileNotice}
              </span>
            </div>
          </div>
        )}

        <DoPreparationsWorkspaceTabs
          activeTab={workspace}
          counts={workspaceCounts}
          onChange={changeWorkspace}
        />

        <DoPreparationsKpiStrip>
          <DoKpiCard
            detail="Belum lengkap atau masih perlu tindak lanjut"
            icon="search"
            label="Perlu Diproses"
            tone="attention"
            value={summary.pending}
          />
          <DoKpiCard
            detail="Fleet Order dengan kebutuhan stok Kurang atau Kosong"
            icon="warning"
            label="Stok Bermasalah"
            tone="danger"
            value={summary.stockProblem}
          />
          <DoKpiCard
            detail="Sebagian dokumen sudah memiliki DO"
            icon="partial"
            label="Sebagian Dibuat"
            tone="warning"
            value={summary.partial}
          />
          <DoKpiCard
            detail="Fleet Order yang membutuhkan perhatian operasional"
            icon="sync"
            label="Perlu Tindak Lanjut"
            value={summary.actionRequired}
          />
        </DoPreparationsKpiStrip>

        <DoPreparationsFilterWorkbench
          activeFilters={
            <DoActiveFilterSummary
              contextLabel={DO_WORKSPACE_LABELS[workspace]}
              labels={activeFilterLabels}
            />
          }
        >
          <ResponsiveFilterPanel label="Filter Pembuatan DO">
            <form
              className={`do-filters do-filters--operational ${
                workspace === "PENDING"
                  ? "do-filters--with-status"
                  : "do-filters--without-status"
              }`}
              onSubmit={applyFilters}
            >
              <label className="search-field">
                <Icon name="search" size={16} />
                <input
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      search: event.target.value,
                    }))
                  }
                  placeholder="Cari FO, SO, pelanggan, driver, nopol, atau rute"
                  value={draft.search}
                />
              </label>

              <select
                aria-label="Depot Asal"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    depotId: event.target.value,
                  }))
                }
                value={draft.depotId}
              >
                <option value="">Semua depot</option>
                {depotOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                aria-label="Nama Driver"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    driverKey: event.target.value,
                  }))
                }
                value={draft.driverKey}
              >
                <option value="">Semua driver</option>
                {driverOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              {workspace === "PENDING" && (
                <select
                  aria-label="Status Proses DO"
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      processStatus: event.target.value,
                    }))
                  }
                  value={draft.processStatus}
                >
                  <option value="">Semua status proses</option>
                  {processStatusOptions.map((value) => (
                    <option key={value} value={value}>
                      {PROCESS_STATUS_LABELS[value]}
                    </option>
                  ))}
                </select>
              )}

              <select
                aria-label="Rute FO"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    foRouteId: event.target.value,
                  }))
                }
                value={draft.foRouteId}
              >
                <option value="">Semua rute FO</option>
                {routeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <input
                aria-label="Tanggal Berangkat"
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    departureDate: event.target.value,
                  }))
                }
                type="date"
                value={draft.departureDate}
              />

              <div className="do-filter-actions">
                <button className="secondary-button" type="submit">
                  <Icon name="filter" size={15} />
                  Terapkan
                </button>
                <button className="text-button" onClick={clearFilters} type="button">
                  Bersihkan
                </button>
              </div>
            </form>
          </ResponsiveFilterPanel>
        </DoPreparationsFilterWorkbench>

        <section className="records-panel do-data-surface">
          <DoPreparationsListHeader
            description={DO_WORKSPACE_DESCRIPTIONS[workspace]}
            title={DO_WORKSPACE_TITLES[workspace]}
          />

          <div className="table-wrap do-list-wrap">
            <table className="do-list-table do-list-table--operational responsive-card-table">
              <thead>
                <tr>
                  <SortableHeader column="foNumber" sort={sort} toggleSort={toggleSort}>
                    Nomor FO
                  </SortableHeader>
                  <SortableHeader
                    column="departureDate"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Perjalanan
                  </SortableHeader>
                  <SortableHeader
                    column="foRouteName"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Rute & Depot
                  </SortableHeader>
                  <SortableHeader
                    column="driverName"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Driver & Armada
                  </SortableHeader>
                  <SortableHeader column="totalSo" sort={sort} toggleSort={toggleSort}>
                    Dokumen
                  </SortableHeader>
                  <SortableHeader
                    column={workspace === "PENDING" ? "stockStatus" : "fleetStatus"}
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    {operationalColumnLabel}
                  </SortableHeader>
                  <SortableHeader column="processStatus" sort={sort} toggleSort={toggleSort}>
                    Status DO
                  </SortableHeader>
                  <SortableHeader
                    column="lastCheckedAt"
                    sort={sort}
                    toggleSort={toggleSort}
                  >
                    Sinkronisasi DO
                  </SortableHeader>
                  <th className="sticky-action-cell">Aksi</th>
                </tr>
              </thead>

              <tbody>
                {!loading &&
                  sortedItems.map((row) => (
                    <tr
                      className={`clickable-table-row do-list-row do-list-row--${row.workspaceStatus.toLowerCase()}`}
                      key={row.id}
                      onClick={(event) => {
                        if (!isInteractiveTarget(event.target)) openDetail(row.id);
                      }}
                      onKeyDown={(event) => {
                        if (
                          (event.key === "Enter" || event.key === " ") &&
                          !isInteractiveTarget(event.target)
                        ) {
                          event.preventDefault();
                          openDetail(row.id);
                        }
                      }}
                      role="button"
                      tabIndex="0"
                    >
                      <td>
                        <div className="do-list-stack">
                          <strong>{row.foNumber}</strong>
                          <small>Dibuat {formatDate(row.createdAt?.slice?.(0, 10))}</small>
                        </div>
                      </td>

                      <td>
                        <div className="do-list-stack">
                          <strong>{formatDate(row.departureDate)}</strong>
                          <small>s.d. {formatDate(row.estimatedReturnDate)}</small>
                        </div>
                      </td>

                      <td>
                        <div className="do-list-stack">
                          <RouteBadge name={row.foRouteName} />
                          <small>Dari: {row.depotName || "Depot belum tersedia"}</small>
                        </div>
                      </td>

                      <td>
                        <div className="do-list-stack">
                          <strong>{displayDriverName(row.driverName)}</strong>
                          <small>{displayVehicle(row.vehiclePlate, row.vehicleType)}</small>
                        </div>
                      </td>

                      <td>
                        {row.totalSo > 0 ? (
                          <div className="do-list-stack do-list-progress">
                            <strong>{row.doCreated}/{row.totalSo} DO</strong>
                            <small>
                              {row.completionPercent}% tersedia
                              {row.outstandingDo > 0
                                ? ` · ${row.outstandingDo} belum dibuat`
                                : " · lengkap"}
                            </small>
                            <span aria-hidden="true" className="do-list-progress__track">
                              <span style={{ width: `${row.completionPercent}%` }} />
                            </span>
                          </div>
                        ) : (
                          <div className="do-list-stack">
                            <strong>Tidak ada Sales Order</strong>
                            <small>Fleet Order tidak dapat diproses</small>
                          </div>
                        )}
                      </td>

                      <td data-label={operationalColumnLabel}>
                        {workspace === "PENDING" ? (
                          <div className="do-list-stack">
                            <StockStatusBadge status={row.stockStatus} />
                            <small>
                              {row.stockStatus === "AMAN"
                                ? "Seluruh kebutuhan FO tercukupi"
                                : row.stockStatus === "KOSONG"
                                  ? "Tidak ada stok untuk kebutuhan aktif"
                                  : row.stockStatus === "KURANG"
                                    ? "Ada kebutuhan FO yang belum terpenuhi"
                                    : row.stockStatus === "NOT_APPLICABLE"
                                      ? "Tidak ada kebutuhan stok aktif"
                                      : "Data stok perlu diperiksa"}
                            </small>
                          </div>
                        ) : (
                          <div className="do-list-stack">
                            <FleetStatusBadge status={row.fleetStatus} />
                            <small>{getFleetStatusMeta(row.fleetStatus).description}</small>
                          </div>
                        )}
                      </td>

                      <td>
                        <div className="do-list-stack">
                          <ProcessStatusBadge status={row.processStatus} />
                          {workspace === "COMPLETED" && <small>Seluruh DO tersedia</small>}
                          {workspace === "CANCELLED" && <small>Kondisi DO saat dibatalkan</small>}
                        </div>
                      </td>

                      <td>
                        <div
                          className={`do-list-stack do-reconciliation-state do-reconciliation-state--${normalizeStatusClass(
                            row.reconciliation?.active
                              ? "CHECKING"
                              : row.reconciliation?.freshnessStatus,
                          )}`}
                        >
                          <strong>
                            {row.reconciliation?.active
                              ? RECONCILIATION_STATUS_LABELS[row.reconciliation.status] || "Sedang memeriksa"
                              : row.reconciliation?.freshnessStatus === "STALE"
                                ? "Perlu diperbarui"
                                : row.reconciliation?.freshnessStatus === "NEVER"
                                  ? "Belum diperiksa"
                                  : "Data terbaru"}
                          </strong>
                          <small>{getReconciliationDescription(row)}</small>
                        </div>
                      </td>

                      <td className="sticky-action-cell">
                        <button
                          className="table-action-button"
                          onClick={(event) => {
                            event.stopPropagation();
                            openDetail(row.id);
                          }}
                          type="button"
                        >
                          Buka
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {loading && <div className="do-list-state">Memuat Fleet Order…</div>}

            {!loading && !sortedItems.length && (
              <div className="do-list-state">Belum ada Fleet Order sesuai filter.</div>
            )}
          </div>

          {!loading && (
            <footer className="do-list-footer">
              <span>
                Menampilkan <strong>{sortedItems.length}</strong> Fleet Order pada konteks{" "}
                <strong>{DO_WORKSPACE_LABELS[workspace]}</strong>.
              </span>
              <span>Gunakan tombol Buka untuk meninjau persiapan Delivery Order.</span>
            </footer>
          )}
        </section>
      </div>
    </main>
  );
}

function DoPreparationDetail({ fleetOrderId, onBack }) {
  const [detail, setDetail] = useState(null);
  const [writeContract, setWriteContract] = useState(null);
  const [writeContractLoading, setWriteContractLoading] = useState(false);
  const [writeContractError, setWriteContractError] = useState("");
  const [writeProfileStatus, setWriteProfileStatus] = useState(null);
  const [writeProfileBusy, setWriteProfileBusy] = useState(false);
  const [writeProfileError, setWriteProfileError] = useState("");
  const [reviewSource, setReviewSource] = useState(null);
  const [writeRequest, setWriteRequest] = useState(null);
  const [writeEvents, setWriteEvents] = useState([]);
  const [writeActionBusy, setWriteActionBusy] = useState("");
  const [writeActionError, setWriteActionError] = useState("");
  const [writeHistoryLoading, setWriteHistoryLoading] = useState(false);
  const [selectedPreparationId, setSelectedPreparationId] = useState(null);
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [reviewIncompleteSignal, setReviewIncompleteSignal] = useState(0);
  const [batchPlan, setBatchPlan] = useState(null);
  const [batchPlanOpen, setBatchPlanOpen] = useState(false);
  const [batchPlanBusy, setBatchPlanBusy] = useState("");
  const [batchPlanError, setBatchPlanError] = useState("");
  const [batchExecution, setBatchExecution] = useState(null);
  const [batchExecutionBusy, setBatchExecutionBusy] = useState("");
  const [batchExecutionError, setBatchExecutionError] = useState("");
  const autoReconcileRequestedRef = useRef(false);
  const pendingBatchPlanAfterStockSyncRef = useRef(false);

  const loadWriteContract = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setWriteContractLoading(true);
    setWriteContractError("");

    try {
      const payload = await getDoWriteContract(fleetOrderId);
      setWriteContract(payload.contract);
      return payload.contract;
    } catch (requestError) {
      setWriteContractError(requestError.message);
    } finally {
      if (!silent) setWriteContractLoading(false);
    }
  }, [fleetOrderId]);

  async function checkWriteProfile() {
    setWriteProfileBusy(true);
    setWriteProfileError("");

    try {
      const payload = await getDoWriteProfileStatus();
      setWriteProfileStatus(payload.profile);
    } catch (requestError) {
      setWriteProfileStatus(null);
      setWriteProfileError(requestError.message);
    } finally {
      setWriteProfileBusy(false);
    }
  }

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setError("");

    try {
      const payload = await getDoPreparationDetail(fleetOrderId);
      setDetail(payload.detail);
      setSelectedPreparationId((current) =>
        payload.detail.preparations.some((item) => item.id === current)
          ? current
          : payload.detail.preparations[0]?.id || null,
      );
      return payload.detail;
    } catch (requestError) {
      if (!silent) setError(requestError.message);
    }
  }, [fleetOrderId]);

  const loadSourceWriteState = useCallback(
    async (source, { silent = false } = {}) => {
      if (!source?.sourceType || !source?.sourceDocumentId) {
        setWriteRequest(null);
        setWriteEvents([]);
        return null;
      }

      if (!silent) {
        setWriteHistoryLoading(true);
        setWriteActionError("");
      }

      try {
        const payload = await getDoSourceWriteHistory(
          fleetOrderId,
          source.sourceType,
          source.sourceDocumentId,
        );
        setWriteRequest(payload.request || null);
        setWriteEvents(payload.events || []);
        return payload;
      } catch (requestError) {
        if (!silent) setWriteActionError(requestError.message);
        return null;
      } finally {
        if (!silent) setWriteHistoryLoading(false);
      }
    },
    [fleetOrderId],
  );

  async function openReviewSource(source) {
    setReviewSource(source);
    setWriteRequest(null);
    setWriteEvents([]);
    setWriteActionError("");
    setWriteActionBusy("");

    if (source?.preparationId && !source?.alreadyCreated) {
      setWriteActionBusy("warehouse");
      try {
        const payload = await refreshDoPreparationLiveStock(
          source.preparationId,
        );
        if (payload.detail) setDetail(payload.detail);
        if (payload.contract) setWriteContract(payload.contract);

        const latestSource = (payload.contract?.sources || []).find(
          (entry) => entry.key === source.key,
        );
        if (latestSource) setReviewSource(latestSource);
        return;
      } catch (requestError) {
        setWriteActionError(
          requestError.message ||
            "Stok terbaru Accurate belum dapat diperiksa.",
        );
        return;
      } finally {
        setWriteActionBusy("");
      }
    }

    const latestContract = await loadWriteContract({ silent: true });
    const latestSource = (latestContract?.sources || []).find(
      (entry) => entry.key === source.key,
    );

    if (latestSource) setReviewSource(latestSource);
  }

  function closeReviewSource() {
    if (writeActionBusy) return;
    setReviewSource(null);
    setWriteRequest(null);
    setWriteEvents([]);
    setWriteActionError("");
  }

  async function refreshAfterSourceWrite(source, result = null) {
    const [detailPayload, contractPayload, historyPayload] = await Promise.all([
      getDoPreparationDetail(fleetOrderId),
      getDoWriteContract(fleetOrderId),
      getDoSourceWriteHistory(
        fleetOrderId,
        source.sourceType,
        source.sourceDocumentId,
      ),
    ]);

    setDetail(detailPayload.detail);
    setWriteContract(contractPayload.contract);
    setBatchPlan(null);
    setWriteRequest(historyPayload.request || result?.request || null);
    setWriteEvents(historyPayload.events || []);

    const refreshedSource = (contractPayload.contract?.sources || []).find(
      (entry) => entry.key === source.key,
    );
    if (refreshedSource) setReviewSource(refreshedSource);

    return {
      contract: contractPayload.contract,
      detail: detailPayload.detail,
      history: historyPayload,
    };
  }

  async function createSourceDeliveryOrder(source, options = {}) {
    setWriteActionBusy("create");
    setWriteActionError("");
    setError("");
    setMessage("");

    try {
      const result = await createDoFromSource(
        fleetOrderId,
        source.sourceType,
        source.sourceDocumentId,
        source.payloadHash,
        options,
      );
      setWriteRequest(result.request || null);
      await refreshAfterSourceWrite(source, result);

      if (result.reconciliationRequired) {
        setMessage(
          "Permintaan sudah dikirim. Periksa hasil di Accurate sebelum melakukan tindakan lain.",
        );
      } else {
        const doNumber = result.request?.accurateDoNumber;
        setMessage(
          doNumber
            ? `Delivery Order ${doNumber} berhasil dibuat.`
            : "Delivery Order berhasil dibuat dan diverifikasi.",
        );
      }
      return true;
    } catch (requestError) {
      if (requestError.details?.request) {
        setWriteRequest(requestError.details.request);
      }
      setWriteActionError(requestError.message);
      await loadSourceWriteState(source, { silent: true });
      return false;
    } finally {
      setWriteActionBusy("");
    }
  }

  async function reconcileSourceDeliveryOrder(source) {
    setWriteActionBusy("reconcile");
    setWriteActionError("");
    setError("");
    setMessage("");

    try {
      const result = await reconcileDoSourceWrite(
        fleetOrderId,
        source.sourceType,
        source.sourceDocumentId,
      );
      setWriteRequest(result.request || null);
      await refreshAfterSourceWrite(source, result);

      if (result.reconciliationRequired) {
        setMessage(
          "Delivery Order belum dapat dipastikan. Jangan membuat ulang sebelum diperiksa administrator.",
        );
      } else {
        const doNumber = result.request?.accurateDoNumber;
        setMessage(
          doNumber
            ? `Delivery Order ${doNumber} ditemukan dan berhasil dihubungkan.`
            : "Hasil pembuatan Delivery Order berhasil diverifikasi.",
        );
      }
      return true;
    } catch (requestError) {
      if (requestError.details?.request) {
        setWriteRequest(requestError.details.request);
      }
      setWriteActionError(requestError.message);
      await loadSourceWriteState(source, { silent: true });
      return false;
    } finally {
      setWriteActionBusy("");
    }
  }

  const loadBatchPlan = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setBatchPlanError("");
    try {
      const payload = await getDoBatchPlan(fleetOrderId);
      setBatchPlan(payload.plan || null);
      return payload.plan || null;
    } catch (requestError) {
      if (!silent) setBatchPlanError(operationalDoError(requestError));
      return null;
    }
  }, [fleetOrderId]);

  const loadBatchExecution = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setBatchExecutionError("");
    try {
      const payload = await getDoBatchExecution(fleetOrderId);
      setBatchExecution(payload.execution || null);
      return payload.execution || null;
    } catch (requestError) {
      if (!silent) setBatchExecutionError(requestError.message);
      return null;
    }
  }, [fleetOrderId]);

  async function startBatchExecution() {
    if (batchExecutionBusy) return;
    setReviewSource(null);
    setBatchExecutionBusy("start");
    setBatchExecutionError("");
    try {
      const payload = await startDoBatchExecution(fleetOrderId);
      setBatchExecution(payload.execution || null);
      setBatchPlanOpen(true);
      setMessage("Antrean Delivery Order dimulai. Proses tetap berjalan walaupun halaman ditutup.");
    } catch (requestError) {
      setBatchExecutionError(requestError.message);
    } finally {
      setBatchExecutionBusy("");
    }
  }

  async function pauseBatchExecution() {
    if (batchExecutionBusy) return;
    setBatchExecutionBusy("pause");
    setBatchExecutionError("");
    try {
      const payload = await pauseDoBatchExecution(fleetOrderId);
      setBatchExecution(payload.execution || null);
      setMessage("Permintaan jeda diterima. SO yang sedang diproses akan diselesaikan terlebih dahulu.");
    } catch (requestError) {
      setBatchExecutionError(requestError.message);
    } finally {
      setBatchExecutionBusy("");
    }
  }

  async function resumeBatchExecution() {
    if (batchExecutionBusy) return;
    setBatchExecutionBusy("resume");
    setBatchExecutionError("");
    try {
      const payload = await resumeDoBatchExecution(fleetOrderId);
      setBatchExecution(payload.execution || null);
      setMessage("Antrean dilanjutkan dari Sales Order berikutnya yang masih menunggu.");
    } catch (requestError) {
      setBatchExecutionError(requestError.message);
    } finally {
      setBatchExecutionBusy("");
    }
  }

  async function terminateBatchExecution() {
    if (batchExecutionBusy) return;
    const confirmed = window.confirm(
      "Hentikan antrean Delivery Order? Dokumen yang sudah berhasil dibuat tetap tersimpan dan tidak dibatalkan.",
    );
    if (!confirmed) return;

    setBatchExecutionBusy("terminate");
    setBatchExecutionError("");
    try {
      const payload = await terminateDoBatchExecution(fleetOrderId);
      setBatchExecution(payload.execution || null);
      setMessage("Permintaan penghentian diterima. SO aktif akan diselesaikan terlebih dahulu bila sedang ditulis.");
    } catch (requestError) {
      setBatchExecutionError(requestError.message);
    } finally {
      setBatchExecutionBusy("");
    }
  }

  const createBatchPlanFromLatestStock = useCallback(async () => {
    setBatchPlanBusy("planning");
    setBatchPlanError("");
    try {
      const payload = await createDoBatchPlan(fleetOrderId);
      setBatchPlan(payload.plan || null);
      setBatchPlanOpen(Boolean(payload.plan));
      return payload.plan || null;
    } catch (requestError) {
      setBatchPlanError(operationalDoError(requestError));
      return null;
    } finally {
      setBatchPlanBusy("");
    }
  }, [fleetOrderId]);

  const triggerScopedStockSync = useCallback(
    () => triggerDoStockSync(fleetOrderId),
    [fleetOrderId],
  );

  const stockSync = useManualSync(triggerScopedStockSync, async (syncRun) => {
    if (String(syncRun?.status || "").toUpperCase() !== "SUCCESS") {
      const syncMessage =
        syncRun?.error_message ||
        syncRun?.errorMessage ||
        "Sinkronisasi stok gudang Fleet Order belum selesai.";
      if (pendingBatchPlanAfterStockSyncRef.current) {
        pendingBatchPlanAfterStockSyncRef.current = false;
        setBatchPlanBusy("");
        setBatchPlanError(
          operationalDoError(
            syncMessage,
            "Sinkronisasi stok gudang Fleet Order belum selesai. Tidak ada rencana atau Delivery Order yang dibuat.",
          ),
        );
      }
      return;
    }

    const response = await recalculateDoStock(fleetOrderId);
    setDetail(response.detail);
    await loadWriteContract({ silent: true });

    if (pendingBatchPlanAfterStockSyncRef.current) {
      pendingBatchPlanAfterStockSyncRef.current = false;
      const plan = await createBatchPlanFromLatestStock();
      if (plan) {
        setMessage("Stok gudang Fleet Order sudah diperbarui dan rencana proses DO berhasil disusun.");
      }
    } else {
      setMessage("Sinkronisasi stok gudang Fleet Order selesai dan status stok diperbarui.");
    }
  });

  async function requestBatchPlan() {
    if (stockSync.syncing || batchPlanBusy || batchExecution?.active) return;
    setBatchPlan(null);
    setBatchPlanOpen(false);
    pendingBatchPlanAfterStockSyncRef.current = true;
    setBatchPlanBusy("stock");
    setBatchPlanError("");
    setMessage("");
    await stockSync.startSync();
  }

  async function saveBatchPlanPriority(
    sourceKeys,
    sourceItemSelections = [],
  ) {
    setBatchPlanBusy("priority");
    setBatchPlanError("");
    try {
      const payload = await updateDoBatchPlanPriority(
        fleetOrderId,
        sourceKeys,
        [],
        sourceItemSelections,
      );
      setBatchPlan(payload.plan || null);
      setMessage("Urutan dan Qty Delivery Order berhasil disimpan.");
    } catch (requestError) {
      setBatchPlanError(requestError.message);
    } finally {
      setBatchPlanBusy("");
    }
  }

  useEffect(() => {
    autoReconcileRequestedRef.current = false;
    load();
    loadWriteContract();
    loadBatchPlan({ silent: true });
    loadBatchExecution({ silent: true });
    checkWriteProfile();
  }, [load, loadBatchExecution, loadBatchPlan, loadWriteContract]);

  useEffect(() => {
    if (!stockSync.syncError || !pendingBatchPlanAfterStockSyncRef.current) return;
    pendingBatchPlanAfterStockSyncRef.current = false;
    setBatchPlanBusy("");
    setBatchPlanError(operationalDoError(stockSync.syncError));
  }, [stockSync.syncError]);

  useEffect(() => {
    const pollingStatuses = [
      "QUEUED",
      "RUNNING",
      "PAUSE_REQUESTED",
      "TERMINATE_REQUESTED",
    ];
    if (!batchExecution || !pollingStatuses.includes(batchExecution.status)) return undefined;

    const timer = window.setInterval(async () => {
      const execution = await loadBatchExecution({ silent: true });
      if (execution && !pollingStatuses.includes(execution.status)) {
        await Promise.all([
          load({ silent: true }),
          loadWriteContract({ silent: true }),
          loadBatchPlan({ silent: true }),
        ]);
      }
    }, 2500);

    return () => window.clearInterval(timer);
  }, [batchExecution?.id, batchExecution?.status, load, loadBatchExecution, loadBatchPlan, loadWriteContract]);

  useEffect(() => {
    if (!reviewSource) return;
    loadSourceWriteState(reviewSource);
  }, [loadSourceWriteState, reviewSource?.key]);

  useEffect(() => {
    if (!reviewSource || !writeContract) return;
    const refreshedSource = (writeContract.sources || []).find(
      (source) => source.key === reviewSource.key,
    );
    if (refreshedSource) setReviewSource(refreshedSource);
  }, [reviewSource?.key, writeContract]);

  useEffect(() => {
    if (
      !reviewSource ||
      !["VALIDATING", "SENDING", "RECONCILING"].includes(
        String(writeRequest?.status || "").toUpperCase(),
      )
    ) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      loadSourceWriteState(reviewSource, { silent: true });
    }, 1500);

    return () => window.clearInterval(interval);
  }, [loadSourceWriteState, reviewSource, writeRequest?.status]);

  useEffect(() => {
    if (
      !detail ||
      autoReconcileRequestedRef.current ||
      detail.summary?.doStatus === "SELESAI" ||
      detail.reconciliation?.active ||
      !["NEVER", "STALE"].includes(detail.reconciliation?.freshnessStatus)
    ) {
      return;
    }

    autoReconcileRequestedRef.current = true;
    startDoReconciliation(fleetOrderId, { freshnessMinutes: 5 })
      .then(() => {
        window.setTimeout(() => load({ silent: true }), 400);
      })
      .catch(() => {
        // Pemeriksaan otomatis tidak boleh menutupi workspace dengan error
        // sementara. Status terakhir tetap tersedia dan user dapat mencoba
        // kembali lewat Perbarui data.
      });
  }, [detail, fleetOrderId, load]);

  useEffect(() => {
    if (!detail?.reconciliation?.active) return undefined;

    const interval = window.setInterval(() => {
      load({ silent: true });
    }, 1500);

    return () => window.clearInterval(interval);
  }, [detail?.reconciliation?.active, load]);

  async function startReconciliation({ fullRecheck = false } = {}) {
    setBusy("do-check");
    setError("");
    setMessage("");

    try {
      const response = await startDoReconciliation(fleetOrderId, {
        force: true,
        fullRecheck,
        freshnessMinutes: 5,
      });
      if (response.skippedFresh || response.retryCooldown) {
        setMessage(response.message);
      }
      await load({ silent: true });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy("");
    }
  }

  async function runAction(name, action) {
    if (batchMutationBlocked()) return;
    setBusy(name);
    setError("");
    setMessage("");

    try {
      const response = await action();
      if (response.detail) setDetail(response.detail);
      setBatchPlan(null);
      await loadWriteContract({ silent: true });
      setMessage(response.message);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy("");
    }
  }


  function batchMutationBlocked() {
    if (!batchExecution?.active) return false;
    setError(
      "Antrean Delivery Order masih aktif. Selesaikan, jeda, atau hentikan antrean sebelum mengubah gudang atau detail dokumen.",
    );
    return true;
  }

  async function refreshAllData() {
    if (batchMutationBlocked()) return;
    setBusy("refresh-all");
    setError("");
    setMessage("");
    setBatchPlan(null);

    try {
      const detailResponse = await syncDoPreparationItems(fleetOrderId);
      if (detailResponse.detail) setDetail(detailResponse.detail);

      if (detailResponse.detail?.depotWarehouse?.mapped || detail?.depotWarehouse?.mapped) {
        const warehouseResponse = await applyDepotWarehouseToDoPreparations(fleetOrderId);
        if (warehouseResponse.detail) setDetail(warehouseResponse.detail);
      }

      await stockSync.startSync();

      await startDoReconciliation(fleetOrderId, {
        force: true,
        freshnessMinutes: 5,
      });
      setMessage("Detail dokumen dan stok berhasil diperbarui.");

      await Promise.all([
        load({ silent: true }),
        loadWriteContract({ silent: true }),
        checkWriteProfile(),
      ]);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy("");
    }
  }

  async function mapDepotWarehouse(warehouseId) {
    if (batchMutationBlocked()) return;
    setBusy("depot-mapping");
    setError("");
    setMessage("");

    try {
      const response = await updateDoDepotWarehouseMapping(
        fleetOrderId,
        warehouseId,
      );

      if (response.detail) setDetail(response.detail);
      if (response.contract) setWriteContract(response.contract);
      setBatchPlan(null);
      setMessage(
        response.message ||
          "Mapping Gudang Accurate depot berhasil diperbarui.",
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy("");
    }
  }

  async function setWarehouseForAll(preparationId, warehouseId) {
    if (batchMutationBlocked()) return;
    await runAction("warehouse", async () => {
      await updateDoPreparationDefaultWarehouse(preparationId, warehouseId);
      const response = await applyDoPreparationWarehouse(preparationId);
      return {
        ...response,
        message: "Gudang berhasil diterapkan ke seluruh item.",
      };
    });
  }

  async function changeItemWarehouse(itemId, warehouseId) {
    if (batchMutationBlocked()) return;
    await runAction("warehouse", () =>
      updateDoPreparationItemWarehouse(itemId, warehouseId),
    );
  }

  async function refreshReviewWarehouseState(source, action, successMessage) {
    if (!source?.key || !source?.preparationId) return false;
    if (batchMutationBlocked()) return false;

    setWriteActionBusy("warehouse");
    setWriteActionError("");
    setError("");
    setMessage("");

    try {
      await action();
      const livePayload = await refreshDoPreparationLiveStock(
        source.preparationId,
      );
      const nextContract = livePayload.contract;

      if (livePayload.detail) setDetail(livePayload.detail);
      if (nextContract) setWriteContract(nextContract);
      setBatchPlan(null);

      const refreshedSource = (nextContract?.sources || []).find(
        (entry) => entry.key === source.key,
      );

      if (refreshedSource) {
        setReviewSource(refreshedSource);
      }

      setMessage(successMessage);
      return true;
    } catch (requestError) {
      setWriteActionError(requestError.message);
      return false;
    } finally {
      setWriteActionBusy("");
    }
  }

  async function changeReviewSourceWarehouse(source, warehouseId) {
    if (!source?.preparationId) return false;

    return refreshReviewWarehouseState(
      source,
      async () => {
        await updateDoPreparationDefaultWarehouse(
          source.preparationId,
          warehouseId,
        );
        return applyDoPreparationWarehouse(source.preparationId);
      },
      "Gudang seluruh Sales Order diperbarui dan stok terbaru Accurate sudah dihitung.",
    );
  }

  async function changeReviewItemWarehouse(source, item, warehouseId) {
    if (!item?.preparationItemId) return false;

    return refreshReviewWarehouseState(
      source,
      () =>
        updateDoPreparationItemWarehouse(
          item.preparationItemId,
          warehouseId,
        ),
      `${item.itemName || item.itemNo || "Item"} dipindahkan ke gudang terpilih dan stok terbaru Accurate sudah dihitung.`,
    );
  }

  if (!detail) {
    return (
      <main className="do-page do-detail-page">
        <div className="do-detail-loading-shell">
          <button className="do-detail-back" onClick={onBack} type="button">
            <Icon name="chevronLeft" size={15} />
            Kembali ke Pembuatan DO
          </button>
          <div className={error ? "error-banner" : "notice-banner"}>
            {error || "Memuat persiapan DO..."}
          </div>
        </div>
      </main>
    );
  }

  const {
    fleetOrder,
    preparations,
    warehouses,
    summary,
    depotWarehouse,
    execution,
    reconciliation,
  } = detail;

  return (
    <main className="do-page do-detail-page">
      <div className="do-detail-workspace do-detail-workspace--full">
        <DoDetailToolbar fleetOrder={fleetOrder} onBack={onBack} />

        <DoDetailCommandCard
          depotWarehouse={depotWarehouse}
          execution={execution}
          fleetOrder={fleetOrder}
          formatDate={formatDate}
          formatTimestamp={formatTimestamp}
          formatVolume={formatVolume}
          formatWeight={formatWeight}
          statusBadge={<DoStatusBadge status={summary.doStatus} />}
          summary={summary}
        />

        <section className="do-preparation-toolbar" aria-label="Kontrol data persiapan DO">
          <div className="do-preparation-toolbar__context">
            <span className="do-preparation-toolbar__icon" aria-hidden="true">
              <Icon name="database" size={17} />
            </span>
            <div>
              <strong>Data Pembuatan DO</strong>
              <span>
                Stok terakhir disinkronkan {formatTimestamp(detail.latestStock?.syncedAt)}
                <span aria-hidden="true"> · </span>
                Gudang: {depotWarehouse?.mappedWarehouseName || "belum ditentukan"}
              </span>
            </div>
          </div>

          <div className="do-preparation-toolbar__actions">
            <button
              className="primary-button"
              disabled={Boolean(busy) || stockSync.syncing || reconciliation?.active || batchExecution?.active}
              onClick={refreshAllData}
              type="button"
            >
              <Icon name="refresh" size={16} />
              {busy === "refresh-all" ? "Memperbarui…" : "Perbarui data"}
            </button>

            <details className="do-refresh-menu">
              <summary className="secondary-button">Aksi data</summary>
              <div>
                <button
                  disabled={Boolean(busy) || Boolean(batchExecution?.active)}
                  onClick={() =>
                    runAction("items", () => syncDoPreparationItems(fleetOrderId))
                  }
                  type="button"
                >
                  Sinkronkan detail dokumen
                </button>
                <button
                  disabled={Boolean(busy) || reconciliation?.active || Boolean(batchExecution?.active)}
                  onClick={() => startReconciliation()}
                  type="button"
                >
                  Periksa status DO di Accurate
                </button>
                <button
                  disabled={Boolean(busy) || !depotWarehouse?.mapped || Boolean(batchExecution?.active)}
                  onClick={() =>
                    runAction("depot-warehouse", () =>
                      applyDepotWarehouseToDoPreparations(fleetOrderId),
                    )
                  }
                  type="button"
                >
                  Terapkan ulang gudang depot
                </button>
                <button
                  disabled={stockSync.syncing || Boolean(batchExecution?.active)}
                  onClick={() => {
                    setBatchPlan(null);
                    setBatchPlanOpen(false);
                    stockSync.startSync();
                  }}
                  type="button"
                >
                  Sinkronkan stok {depotWarehouse?.mappedWarehouseName || "gudang FO"}
                </button>
              </div>
            </details>
          </div>
        </section>

        {(error || stockSync.syncError || message) && (
          <div className="do-feedback-stack" aria-live="polite">
            {(error || stockSync.syncError) && (
              <div className="do-feedback do-feedback--error" role="alert">
                <Icon name="warning" size={17} />
                <span>{error || stockSync.syncError}</span>
                <button
                  aria-label="Tutup pesan error"
                  onClick={() => setError("")}
                  type="button"
                >
                  <Icon name="close" size={15} />
                </button>
              </div>
            )}
            {message && (
              <div className="do-feedback do-feedback--success" role="status">
                <Icon name="check" size={17} />
                <span>{message}</span>
                <button
                  aria-label="Tutup pesan"
                  onClick={() => setMessage("")}
                  type="button"
                >
                  <Icon name="close" size={15} />
                </button>
              </div>
            )}
          </div>
        )}

        {reconciliation?.active && (
          <section
            className="do-reconciliation-detail do-reconciliation-detail--checking"
            aria-live="polite"
          >
            <span className="do-reconciliation-detail__icon" aria-hidden="true">
              <Icon name="sync" size={17} />
            </span>
            <div className="do-reconciliation-detail__copy">
              <strong>Memeriksa Delivery Order di Accurate</strong>
              <span>
                {`${reconciliation.resolvedCount}/${reconciliation.targetCount} dokumen ditemukan memiliki DO · ${reconciliation.scannedPages}/${reconciliation.pageCount || "?"} halaman diperiksa.`}
              </span>
            </div>
            <span className="do-reconciliation-detail__progress" aria-hidden="true">
              <span
                style={{
                  width: `${reconciliation.targetCount
                    ? Math.min(
                        100,
                        Math.round(
                          (reconciliation.resolvedCount /
                            reconciliation.targetCount) *
                            100,
                        ),
                      )
                    : 0}%`,
                }}
              />
            </span>
          </section>
        )}

        <DoWriteContractPreview
          contract={writeContract}
          contractError={writeContractError}
          depotWarehouse={depotWarehouse}
          loading={writeContractLoading}
          mappingBusy={busy === "depot-mapping" || Boolean(batchExecution?.active)}
          onCheckProfile={checkWriteProfile}
          onMapWarehouse={mapDepotWarehouse}
          onRetryContract={() => loadWriteContract()}
          profileBusy={writeProfileBusy}
          profileError={writeProfileError}
          profileStatus={writeProfileStatus}
          warehouses={warehouses}
        />

        <DoBatchPlanPanel
          busy={Boolean(batchPlanBusy) || batchExecutionBusy || stockSync.syncing}
          error={batchPlanError || batchExecutionError}
          execution={batchExecution}
          onCreate={requestBatchPlan}
          onOpen={() => setBatchPlanOpen(true)}
          onPauseExecution={pauseBatchExecution}
          onResumeExecution={resumeBatchExecution}
          onSavePriority={saveBatchPlanPriority}
          onStartExecution={startBatchExecution}
          onTerminateExecution={terminateBatchExecution}
          plan={batchPlan}
          progressStage={
            stockSync.syncing || batchPlanBusy === "stock"
              ? "STOCK"
              : batchPlanBusy === "planning"
                ? "PLANNING"
                : ""
          }
          warehouseName={
            depotWarehouse?.mappedWarehouseName || "gudang Fleet Order"
          }
        />

        <DoPreparationMasterDetail
          busy={busy || (batchExecution?.active ? "batch-execution" : "")}
          contract={writeContract}
          onItemWarehouseChange={changeItemWarehouse}
          onSetWarehouseForAll={setWarehouseForAll}
          onReviewSource={openReviewSource}
          onSelect={setSelectedPreparationId}
          preparations={preparations}
          selectedPreparationId={selectedPreparationId}
          stockSyncedAt={detail.latestStock?.syncedAt}
          warehouses={warehouses}
        />

        <details className="do-operational-context">
          <summary>Informasi Fleet Order</summary>
          <div>
            <DoOperationalCheckpoints
              contract={writeContract}
              execution={execution}
              fleetOrder={fleetOrder}
              preparations={preparations}
              summary={summary}
            />

            <DoOperationalException
              execution={execution}
              onReview={() => {
                setReviewIncompleteSignal((current) => current + 1);
                window.requestAnimationFrame(() => {
                  document.querySelector(".do-unified-workspace")?.scrollIntoView({
                    block: "start",
                    behavior: "smooth",
                  });
                });
              }}
              summary={summary}
            />
          </div>
        </details>

        {batchPlanOpen && batchPlan && (
          <DoBatchPlanDialog
            busy={batchPlanBusy === "priority" || Boolean(batchExecutionBusy)}
            error={batchPlanError || batchExecutionError}
            execution={batchExecution}
            onClose={() => setBatchPlanOpen(false)}
            onPauseExecution={pauseBatchExecution}
            onResumeExecution={resumeBatchExecution}
            onSavePriority={saveBatchPlanPriority}
            onStartExecution={startBatchExecution}
            onTerminateExecution={terminateBatchExecution}
            plan={batchPlan}
          />
        )}

        {reviewSource && (
          <DoWriteSourcePreviewDialog
            fleetOrderWarehouse={depotWarehouse}
            onClose={closeReviewSource}
            onCreate={createSourceDeliveryOrder}
            onItemWarehouseChange={changeReviewItemWarehouse}
            onReconcile={reconcileSourceDeliveryOrder}
            onSourceWarehouseChange={changeReviewSourceWarehouse}
            source={reviewSource}
            warehouses={warehouses}
            writeBusy={writeActionBusy}
            writeError={writeActionError}
            writeEvents={writeEvents}
            writeLoading={writeHistoryLoading}
            writeRequest={writeRequest}
          />
        )}

        {summary.totalSo === 0 ? (
          (writeContract?.summary?.advanceInvoices || 0) > 0 ? (
            <section className="do-empty-fo-warning do-empty-fo-warning--info">
              <Icon name="database" size={22} />
              <div>
                <strong>Fleet Order ini hanya memiliki Faktur Dimuka.</strong>
                <p>
                  Faktur Dimuka tersedia pada workspace dokumen sumber di atas.
                </p>
              </div>
            </section>
          ) : (
            <section className="do-empty-fo-warning">
              <Icon name="warning" size={22} />
              <div>
                <strong>Fleet Order tidak memiliki Sales Order aktif maupun Faktur Dimuka.</strong>
                <p>
                  Dokumen tidak dapat diproses. Periksa kembali hubungan Sales Order atau Faktur Dimuka pada Fleet Order.
                </p>
              </div>
            </section>
          )
        ) : null}

      </div>
    </main>
  );
}

export function DoPreparationsPage() {
  const initialListStateRef = useRef(readDoListReturnState() || {});
  const [selectedFleetOrderId, setSelectedFleetOrderId] = useState(() =>
    readDoDetailLocation(),
  );
  const listScrollYRef = useRef(
    Number(
      initialListStateRef.current.scrollY ??
        window.history.state?.doListScrollY,
    ) || 0,
  );

  useEffect(() => {
    let restoreFrameId = null;

    function syncLocation() {
      if (!window.location.pathname.startsWith("/pembuatan-do")) {
        if (restoreFrameId !== null) {
          window.cancelAnimationFrame(restoreFrameId);
          restoreFrameId = null;
        }
        return;
      }

      const nextId = readDoDetailLocation();
      setSelectedFleetOrderId(nextId);

      if (nextId) {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      } else {
        clearDoListReturnState();
        restoreFrameId = window.requestAnimationFrame(() => {
          restoreFrameId = null;
          window.scrollTo({
            top: listScrollYRef.current,
            left: 0,
            behavior: "auto",
          });
        });
      }
    }

    window.addEventListener("route-planning:location-changed", syncLocation);

    return () => {
      window.removeEventListener("route-planning:location-changed", syncLocation);
      if (restoreFrameId !== null) {
        window.cancelAnimationFrame(restoreFrameId);
      }
    };
  }, []);

  function openDetail(id, listState) {
    if (!id) return;

    listScrollYRef.current = Number(listState?.scrollY) || window.scrollY || 0;
    initialListStateRef.current = {
      ...(listState || {}),
      scrollY: listScrollYRef.current,
    };
    saveDoListReturnState(initialListStateRef.current);

    const nextUrl = buildDoDetailUrl(id);
    const nextState = {
      routePlanningView: "do-preparation-detail",
      doFleetOrderId: String(id),
      fromDoPreparationList: true,
      doListScrollY: listScrollYRef.current,
    };

    window.history.pushState(nextState, "", nextUrl);
    publishCommittedLocation(nextUrl, nextState);
    setSelectedFleetOrderId(String(id));
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }

  function closeDetail() {
    clearDoListReturnState();

    const nextState = {
      routePlanningView: "do-preparation-list",
      restoredFromDoPreparationDetail: true,
      doListScrollY: listScrollYRef.current,
    };

    window.history.pushState(nextState, "", "/pembuatan-do");
    publishCommittedLocation("/pembuatan-do", nextState);
    setSelectedFleetOrderId(null);

    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: listScrollYRef.current,
        left: 0,
        behavior: "auto",
      });
    });
  }

  return selectedFleetOrderId ? (
    <DoPreparationDetail
      fleetOrderId={selectedFleetOrderId}
      onBack={closeDetail}
    />
  ) : (
    <DoPreparationList
      initialListState={initialListStateRef.current}
      onOpen={openDetail}
    />
  );
}
